import React, { useMemo, useState } from "react";
import { Button, CircularProgress, IconButton, InputAdornment, TextField } from "@material-ui/core";
import Helpers from "@/components/Helpers";
import ReusableModal from "../ReusableModal";
import Icon from "./icons";
import { T, cardShadow } from "./tokens";

type Field = "currentPassword" | "password" | "confirmPassword";

interface Props {
  /** Injected firebase instance (compat) from withFirebase. */
  firebase: any;
}

const FIELDS: { key: Field; label: string; placeholder: string }[] = [
  { key: "currentPassword", label: "Current Password", placeholder: "Enter your current password" },
  { key: "password", label: "New Password", placeholder: "Create a new password" },
  { key: "confirmPassword", label: "Confirm New Password", placeholder: "Re-enter your new password" },
];

/**
 * Restyled Change Password. Keeps the existing behaviour: re-authenticate with
 * the current password, update it, then sign the user out (matches the legacy
 * desktop ChangePassword tab). Validation reuses Helpers.validator.
 */
const ChangePasswordCard: React.FC<Props> = ({ firebase }) => {
  const helpers = useMemo(() => new Helpers(), []);
  const [values, setValues] = useState<Record<Field, string>>({
    currentPassword: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [visible, setVisible] = useState<Record<Field, boolean>>({
    currentPassword: false,
    password: false,
    confirmPassword: false,
  });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<{ show: boolean; title: string; content: string; success: boolean }>({
    show: false,
    title: "",
    content: "",
    success: false,
  });

  const onChange = (key: Field, value: string) => {
    const nextValues = { ...values, [key]: value };
    setValues(nextValues);
    const result = helpers.validator(nextValues, key);
    setErrors((prev) => {
      const next = { ...prev };
      if (result) next[result.name] = result.error;
      if (key === "password") {
        const confirm = helpers.validator(nextValues, "confirmPassword");
        if (confirm) next[confirm.name] = confirm.error;
      }
      return next;
    });
  };

  const closeModal = () => {
    const wasSuccess = modal.success;
    setModal((m) => ({ ...m, show: false }));
    if (wasSuccess) firebase.doSignOut();
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const noErrors = ["currentPassword", "password", "confirmPassword"].every(
      (name) => (errors[`${name}Error`] || "") === ""
    );
    const filled = values.currentPassword !== "" && values.password !== "" && values.confirmPassword !== "";
    if (!noErrors || !filled) return;

    const user = firebase.auth.currentUser;
    setLoading(true);
    user
      .reauthenticateWithCredential(
        firebase.emailAuthProvider.credential(user.email, values.currentPassword)
      )
      .then(() => user.updatePassword(values.password))
      .then(() => {
        setModal({
          show: true,
          success: true,
          title: "Success!",
          content: "Looks good! You have successfully changed your password. Please sign in again.",
        });
        setValues({ currentPassword: "", password: "", confirmPassword: "" });
      })
      .catch((error: any) => {
        const content =
          error.code === "auth/wrong-password"
            ? "You have entered an invalid password."
            : error.message;
        setModal({ show: true, success: false, title: "Oops! Please try again.", content });
      })
      .finally(() => setLoading(false));
  };

  return (
    <div
      style={{
        background: "#fff",
        border: `1.5px solid ${T.hairline}`,
        borderRadius: 16,
        padding: "30px 32px",
        boxShadow: cardShadow,
      }}
    >
      <ReusableModal
        show={modal.show}
        title={modal.title}
        content={modal.content}
        button={
          <Button
            variant="contained"
            onClick={closeModal}
            fullWidth
            style={{ background: T.gold, color: "#fff" }}
          >
            OK
          </Button>
        }
      />

      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <span style={{ width: 46, height: 46, borderRadius: 12, background: T.mint2, display: "grid", placeItems: "center" }}>
          <Icon name="lock" size={22} color={T.green} />
        </span>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <h3 style={{ margin: 0, fontWeight: 700, fontSize: 21, lineHeight: 1.1, color: T.heading, letterSpacing: "-.01em" }}>
            Change Password
          </h3>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.4, color: T.muted }}>
            Choose a strong password you don&apos;t reuse elsewhere.
          </p>
        </div>
      </div>

      <div style={{ height: 1, background: T.hairline2, margin: "22px 0 26px" }} />

      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 22, maxWidth: 580 }}>
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label style={{ display: "block", fontWeight: 600, fontSize: 13, color: T.body, marginBottom: 8 }}>
              {f.label}
            </label>
            <TextField
              type={visible[f.key] ? "text" : "password"}
              name={f.key}
              value={values[f.key]}
              placeholder={f.placeholder}
              variant="outlined"
              fullWidth
              autoComplete="off"
              error={Boolean(errors[`${f.key}Error`])}
              helperText={errors[`${f.key}Error`] || ""}
              onChange={(e) => onChange(f.key, e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => setVisible((v) => ({ ...v, [f.key]: !v[f.key] }))}
                    >
                      <Icon name={visible[f.key] ? "eyeOff" : "eye"} size={20} color={T.muted} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
        ))}

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            background: "#F7F8FA",
            border: `1px solid ${T.hairline2}`,
            borderRadius: 10,
            padding: "13px 16px",
          }}
        >
          <Icon name="info" size={17} color="#A0A0A0" style={{ marginTop: 1 }} />
          <span style={{ fontSize: 12.5, lineHeight: 1.5, color: T.muted }}>
            Use at least 8 characters with a mix of upper- and lower-case letters, a number and a symbol.
          </span>
        </div>

        <div>
          {loading ? (
            <CircularProgress style={{ color: T.gold }} />
          ) : (
            <Button
              type="submit"
              style={{
                background: T.gold,
                color: "#fff",
                height: 48,
                padding: "0 30px",
                borderRadius: 9,
                fontWeight: 700,
                fontSize: 14,
                letterSpacing: ".02em",
              }}
            >
              SAVE CHANGES
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ChangePasswordCard;
